import { useEffect } from "react";
import {
  Breed,
  ContactDetailsEventArgs,
  RememberedContactDetails,
  Species
} from "../../../types";
import {
  validateClientData,
  validatePatientData
} from "../helpers/validateContactDetails";
import { UseContactDetailsValidationReturn } from "./useContactDetailsValidation";

interface UseContactDetailsRestorationProps {
  loadContactDetails: () => RememberedContactDetails | null;
  /**
   * Contact details already submitted during the current booking, ie when the user returns
   * to this step to change something. Takes precedence over remembered details.
   */
  existingContactDetails?: ContactDetailsEventArgs;
  validation: UseContactDetailsValidationReturn;
  onRememberDetailsCheckedChange: (checked: boolean) => void;
  onSpeciesSaved: (species?: Species) => void;
  onBreedSaved: (breed?: Breed) => void;
  onRestorationComplete: () => void;
}

/**
 * Whether the booking flow holds contact details that the user has submitted. The booking
 * flow can also hold partial details with only a species, which are stored for species
 * compatibility checks before the form has been submitted.
 */
const hasSubmittedContactDetails = (details?: ContactDetailsEventArgs) =>
  !!(details?.client?.firstName || details?.client?.lastName || details?.patient?.name);

/**
 * Hook to handle restoration of contact details, either from the current booking (when
 * the user returns to the step) or from localStorage (remembered details).
 * Loads data, validates it, and updates the validation state.
 */
export function useContactDetailsRestoration({
  loadContactDetails,
  existingContactDetails,
  validation,
  onRememberDetailsCheckedChange,
  onSpeciesSaved,
  onBreedSaved,
  onRestorationComplete
}: UseContactDetailsRestorationProps) {
  useEffect(() => {
    const saved = loadContactDetails();

    // Details entered during this booking take precedence over remembered details, so that
    // returning to this step (eg via "change" in the summary) doesn't clear the form.
    const source = hasSubmittedContactDetails(existingContactDetails)
      ? existingContactDetails
      : saved;

    if (!source) {
      // No data to restore, restoration is complete immediately
      onRestorationComplete();
      return;
    }

    // Prepare client and patient data
    const clientData = {
      identificationNumber: source.client?.identificationNumber || "",
      firstName: source.client?.firstName || "",
      lastName: source.client?.lastName || "",
      email: source.client?.email || "",
      phoneNumber: source.client?.phoneNumber || "",
      streetAddress: source.client?.streetAddress || "",
      zipCode: source.client?.zipCode || "",
      city: source.client?.city || ""
    };

    const patientData = {
      name: source.patient?.name || "",
      notes: source.patient?.notes || ""
    };

    // Set the data
    validation.setClient(prev => ({ ...prev, ...clientData }));
    validation.setPatient(prev => ({ ...prev, ...patientData }));

    // Validate and set validation state
    const clientValidation = validateClientData(clientData);
    const patientValidation = validatePatientData(patientData);

    validation.setClientValidation(prev => ({
      ...prev,
      ...clientValidation
    }));

    validation.setPatientValidation(prev => ({
      ...prev,
      ...patientValidation
    }));

    // Update the remember details checkbox
    onRememberDetailsCheckedChange(!!saved?.rememberedDetailsChecked);

    if (source !== saved) {
      // Species and breed come from the current booking (see useSpeciesAndBreeds), so
      // there is nothing more to restore.
      onRestorationComplete();
      return;
    }

    // Store saved species/breed for later restoration
    onSpeciesSaved(saved.patient?.species);
    onBreedSaved(saved.patient?.breed);

    // Note: We don't call onRestorationComplete here
    // That will be handled by useSpeciesBreedRestoration after species/breed are restored

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
